'use client'

import { useTranslations } from 'next-intl'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'
import { ChangeEvent, MouseEvent as ReactMouseEvent, useEffect, useState, useTransition } from 'react'

import Icon from '@/components/ui/icon'
import RadioCustom from '@/components/ui/Radio'
import { cleanText, cn } from '@/lib/utils'
import { CategoryProperty, SupplierFilter } from '@/types/category.type'

type FilterItemProps = {
  type: 'brand' | 'prid' | 'stock'
  suppliers: SupplierFilter[] | null
  categoryProperty: CategoryProperty | null
  onClose: () => void
  isPridDisable?: (id: number) => boolean
  prid?: number[]
  totalInStock: number
  totalProduct: number
  setPrid?: React.Dispatch<React.SetStateAction<number[]>>
  ref?: React.Ref<HTMLDivElement>
}

type Formated = {
  id: number
  name: string
  count: number
}

export default function FilterItem({ref, categoryProperty, suppliers, type, onClose, isPridDisable, setPrid, totalInStock, totalProduct }: FilterItemProps) {
  const [data, setData] = useState<Formated[]>([])
  const [filteredData, setFilteredData] = useState<Formated[]>([])
  const [selected, setSelected] = useState<number[]>([])
  const [filterVal, setFilterVal] = useState('')
  const [, startTransition ] = useTransition()

  const t = useTranslations('product')
  const router = useRouter()
  const pathname = usePathname()
  const searchParams = useSearchParams()

  const isSelected = (id: number) => selected.includes(id)

  const updateParamsBySelected = (nextSelected: number[]) => {
    const params = new URLSearchParams(searchParams)
    params.delete('page')

    if (type === 'brand') {
      if (nextSelected.length === 0) {
        params.delete('brand')
      } else {
        params.set('brand', [...new Set(nextSelected)].join(','))
      }
    }

    if (type === 'prid') {
      const pridParam = (searchParams.get('prid') ?? '').split(',').filter(Boolean).map(Number)
      const pridLocal = categoryProperty?.childrens.map((c) => c.id) ?? []
      const finalPrid = [...new Set(getFinalPrid(pridParam, pridLocal, nextSelected))]

      if (finalPrid.length === 0) {
        params.delete('prid')
      } else {
        params.set('prid', finalPrid.join(','))
      }
    }

    startTransition(() => {
      router.replace(`${pathname}?${params.toString().replace(/%2C/g, ',')}`)
    })
  }

  const handleSelected = (e: ReactMouseEvent<HTMLLIElement, MouseEvent>, id: number) => {
    e.stopPropagation()

    if (disablePrid(id)) return

    if (type === 'stock') {
      // Nếu là hàng còn thì chỉ cho chọn 1 item và lọc luôn
      const currentStockParam = searchParams.get('in_stock')
      const isSameAsCurrent = currentStockParam === id.toString()

      setSelected(isSameAsCurrent ? [] : [id])

      const params = new URLSearchParams(searchParams)
      params.delete('page')

      if (isSameAsCurrent) {
        params.delete('in_stock')
      } else {
        params.set('in_stock', id.toString())
      }

      onClose()
      startTransition(() => {
        router.replace(`${pathname}?${params.toString()}`)
      })
    } else {
      const nextSelected = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id]

      setSelected(nextSelected)
      updateParamsBySelected(nextSelected)

      if (type === 'prid' && typeof setPrid === 'function') {
        const pridLocal = categoryProperty?.childrens.map((c) => c.id) ?? []
        setPrid((prev) => [...new Set(getFinalPrid(prev, pridLocal, nextSelected))])
      }
    }
  }

  const handleFilter = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setFilterVal(value)

    if (value === '') {
      setFilteredData(data) // Nếu input rỗng, hiển thị lại toàn bộ data gốc
    } else {
      setFilteredData(data.filter((d) => cleanText(d.name).toLowerCase().includes(cleanText(value).toLowerCase())))
    }
  }

  const getFinalPrid = (arr1: number[], arr2: number[], arr3: number[]) => {
    // Bước 1: Xóa tất cả ID của thuộc tính khỏi arr1
    const updatedArr1 = arr1.filter((id) => !arr2.includes(id))

    // Bước 2: Thêm các ID từ arr3 vào arr1
    updatedArr1.push(...arr3)

    return updatedArr1
  }

  const disablePrid = (id: number) => {
    if (typeof isPridDisable === 'function') {
      return isPridDisable(id)
    }

    return false
  }

  useEffect(() => {
    let newData: Formated[] = []

    if (suppliers) {
      newData = [...newData, ...suppliers.map((s) => ({ id: s.id, name: s.name, count: Number(s.products_count) }))]
    }

    if (categoryProperty?.childrens) {
      newData = [...newData, ...categoryProperty.childrens.map((c) => ({ id: c.id, name: c.name, count: c.actual_count > 0 ? c.actual_count : c.products_count }))]
    }

    if (!suppliers && !categoryProperty?.childrens) {
      newData = [...newData, { id: 0, name: t('all'), count: Number(totalProduct) }, { id: 1, name: t('has_stock'), count: Number(totalInStock) }]
    }

    if (newData.length > 0) {
      setData(newData)
      setFilteredData(newData)
    }
  }, [suppliers, categoryProperty?.childrens, t, totalInStock, totalProduct])

  const titleName = () => {
    if (type === 'brand') {
      return t('supplier')
    } else if (type === 'prid') {
      return categoryProperty?.name
    } else {
      return t('has_stock')
    }
  }

  useEffect(() => {
    if (type === 'brand') {
      const arr1 = (searchParams.get('brand') ?? '').split(',').filter(Boolean).map(Number)
      const arr2 = suppliers?.map((s) => s.id) ?? []
      const set2 = new Set(arr2)
      const commonNumbers = arr1.filter((num) => set2.has(num))
      setSelected(commonNumbers)
    } else if (type === 'prid') {
      const arr1 = (searchParams.get('prid') ?? '').split(',').filter(Boolean).map(Number)
      const arr2 = categoryProperty?.childrens.map((c) => c.id) ?? []
      const set2 = new Set(arr2)
      const commonNumbers = arr1.filter((num) => set2.has(num))
      setSelected(commonNumbers)
    } else if (type === 'stock') {
      const stockParam = searchParams.get('in_stock')

      if (stockParam === null) {
        setSelected([])
      } else {
        const stock = Number(stockParam)
        setSelected(Number.isNaN(stock) ? [] : [stock])
      }
    }
  }, [categoryProperty?.childrens, searchParams, suppliers, type])

  return (
    <div ref={ref} className="absolute -top-[30px] left-[calc(100%+10px)] min-w-[270px] max-w-[270px] bg-white border border-bordercolor rounded z-20 shadow-border">
      <div className="absolute w-3 h-3 bg-white rotate-45 -left-2 top-[41px] border-b border-l border-bordercolor"></div>
      <div className="flex items-center justify-between px-2 py-1.5 border-b border-b-bordercolor">
        <span className="text-[15px] font-semibold text-textcolor opacity-80">{titleName()}</span>
        <div className="flex items-center gap-2">
          {/* {['brand', 'prid'].includes(type) && (
            <div
              onClick={(e) => handleApply(e)}
              className={cn('text-xs px-2 py-1 rounded cursor-pointer', {
                'bg-[#f5f5f5] text-[#999]': !isEnable,
                'bg-green-700 text-white': isEnable,
              })}
            >
              {t('apply')}
            </div>
          )} */}
          <div
            className="flex items-center cursor-pointer text-textcolor opacity-80"
            onClick={(e) => {
              e.stopPropagation()
              onClose()
            }}
          >
            <Icon icon="tgic icon-times-bold" />
          </div>
        </div>
      </div>
      <div className="p-3">
        {['brand', 'prid'].includes(type) && (
          <div className="mb-4 relative">
            <input
              type="text"
              value={filterVal}
              onChange={handleFilter}
              className="outline-none border border-bordercolor w-full rounded pl-2 pr-7 py-1 hover:border-primary"
              placeholder={type === 'brand' ? `${t('find')} ${t('supplier')}` : `${t('find')} ${categoryProperty?.name}`}
            />
            <div className="absolute top-1/2 -translate-y-1/2 right-2 bg-white">
              <Icon icon="tgic icon-search" />
            </div>
          </div>
        )}
        <div className="max-h-[350px] overflow-y-auto scrollbar-thin">
          <ul>
            {/* {filteredData.map((d) => (
              <li
                key={`filter-item-${d.id}`}
                className={cn(
                  'bg-[#f6f6f6] p-1.5 mb-2 text-sm border border-transparent border-l-2 border-l-transparent hover:border-l-primary cursor-pointer hover:text-primary',
                  {
                    'border border-red-500 text-red-500 bg-red-50': isSelected(d.id),
                    'opacity-40 cursor-default hover:border-l-transparent hover:text-current': disablePrid(d.id),
                  },
                )}
                onClick={(e) => handleSelected(e, d.id)}
              >
                <div>ok</div>
                {d.name} {d.count > 0 && <span className="text-primary">({d.count})</span>}
              </li>
            ))} */}
            {filteredData.filter((d) => d.count > 0).map((d) => (
              <li
                key={`filter-item-${d.id}`}
                className={cn(
                  'group flex items-center gap-3 p-1.5 mb-2 text-sm border border-transparent cursor-pointer hover:text-primary',
                  {
                    // 'border border-red-500 text-red-500 bg-red-50': isSelected(d.id),
                    'opacity-40 hidden cursor-default hover:border-l-transparent hover:text-current':
                      disablePrid(d.id),
                  },
                )}
                onClick={(e) => handleSelected(e, d.id)}
              >
                <RadioCustom
                  shape="square"
                  checked={isSelected(d.id)}
                  id={d.id.toString()}
                  disabled={disablePrid(d.id)}
                  className=""
                />
                <div>
                  {d.name} {d.count > 0 && <span className="text-primary">({d.count})</span>}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  )
}
